'use client';

import React, { useState } from 'react';
import { Bell, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { useRouter } from 'next/navigation';
import { useBasePath } from '@/providers/BasePathProvider';
import { useAuth } from '@/providers/AuthProvider';
import { UsersApi } from '@/lib/Apis/apis/users-api';
import { AXIOS_CONFIG } from '@/lib/Apis/sharedConfig';

export function AccountSettingsTab() {
    const router = useRouter();
    const { buildPath } = useBasePath();
    const { logout } = useAuth();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const handleDeleteAccount = async () => {
        setIsDeleting(true);
        setDeleteError(null);
        try {
            const usersApi = new UsersApi(AXIOS_CONFIG);
            await usersApi.deleteMyProfile();
            // Logout and redirect to home
            logout();
            router.push(buildPath('/'));
        } catch (error: any) {
            setDeleteError(
                error?.response?.data?.message || 'Failed to delete account. Please try again.'
            );
            setIsDeleting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Bell className="h-5 w-5 text-hsecondary" />
                    <h3 className="text-lg font-semibold text-hsecondary">Notifications</h3>
                </div>
                <div className="space-y-3">
                    <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                        <div>
                            <p className="text-sm font-medium text-hsecondary">Notification Preferences</p>
                            <p className="text-xs text-hmuted">Manage your notification settings</p>
                        </div>
                        <Button
                            variant="outline-solid"
                            size="sm"
                            onClick={() => router.push(buildPath('/account/notifications'))}
                        >
                            Manage
                        </Button>
                    </div>
                </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="space-y-3">
                    <div className="flex items-center justify-between rounded-lg border border-red-100 bg-red-50 px-4 py-3">
                        <div>
                            <p className="text-sm font-medium text-red-900">Delete Account</p>
                            <p className="text-xs text-red-600">Permanently delete your account and data</p>
                        </div>
                        <Button
                            variant="outline-solid"
                            size="sm"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            onClick={() => setShowDeleteModal(true)}
                        >
                            <Trash2 className="h-4 w-4" />
                            Delete
                        </Button>
                    </div>
                </div>
            </div>

            {/* Delete Account Confirmation Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setDeleteError(null);
                }}
                title="Delete Account"
            >
                <div className="space-y-4">
                    <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
                        <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-sm font-semibold text-red-900">Warning: This action is permanent</p>
                            <p className="text-sm text-red-700 mt-1">
                                Deleting your account will permanently remove all your data, including:
                            </p>
                            <ul className="text-sm text-red-700 mt-2 ml-4 list-disc space-y-1">
                                <li>Personal information and profile</li>
                                <li>Order history and tracking</li>
                                <li>Saved addresses and payment methods</li>
                                <li>Wishlist items</li>
                            </ul>
                        </div>
                    </div>

                    {deleteError && (
                        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            <span className="font-semibold">Error: </span>
                            {deleteError}
                        </div>
                    )}

                    <p className="text-sm text-slate-600">
                        Are you absolutely sure you want to delete your account? This action cannot be undone.
                    </p>

                    <div className="flex gap-3 pt-2">
                        <Button
                            variant="outline-solid"
                            onClick={() => {
                                setShowDeleteModal(false);
                                setDeleteError(null);
                            }}
                            disabled={isDeleting}
                            className="flex-1"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteAccount}
                            isLoading={isDeleting}
                            disabled={isDeleting}
                            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                        >
                            {isDeleting ? 'Deleting...' : 'Delete Account'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
